import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { SearchableSelect } from '@folio/components/ui/searchable-select';
import { getBusinessRoleId } from '@folio/utils/stop-reasons';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyStopReasons } from '@folio/utils/usePolicyStopReasons';
import { useSuspenseQuery } from '@tanstack/react-query';
import { legacyPermissionsControllerAtMeQueryOptions } from 'korint-sdk';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { FunnelStepHeader } from '../../components/layout/FunnelStepHeader';
import { StepperFooter } from '../../components/navigation/stepper-footer/stepper-footer';
import { formatDate } from '../../lib/date-formatting';
import { asksForADate, resolveEffectiveDate } from './effective-date';
import { buildReasonOptions } from './reason-options';
import { useStopDraft } from './useStopDraft';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function ReasonStep({
  documentsPath,
  recapPath,
}: {
  documentsPath: string;
  recapPath: string;
}) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const { next } = useStepperContext();
  const navigate = useNavigate();
  const params = useParams();
  const { draft, save } = useStopDraft(policy.id);
  // Derived, not seeded: the draft resolves through a non-suspense query, so a cold entry on this
  // step would freeze the initializer at undefined and lose the saved reason.
  const [picked, setPicked] = useState<string>();
  const selected = picked ?? draft?.reason;
  const [saving, setSaving] = useState(false);

  const { stopReasonConfigs } = usePolicyStopReasons({
    productId: policy.productId,
    policy: {
      startedAt: policy.startedAt,
      nextRenewalAt: policy.nextRenewalAt,
      suspendedAt: policy.suspendedAt,
    },
  });

  // Suspense, not useQuery: a pending user would filter against no role and briefly offer only the
  // reasons open to everyone. Filtered here rather than in usePolicyStopReasons, whose list the
  // later steps look a saved draft's reason up in.
  const { data: user } = useSuspenseQuery(legacyPermissionsControllerAtMeQueryOptions());
  const businessRoleId = getBusinessRoleId({ user, policy });
  // Destructured so the memo keys off primitives — usePolicyOnDashboard returns a fresh object.
  const { productId, startedAt, nextRenewalAt, suspendedAt } = policy;
  const options = useMemo(
    () =>
      buildReasonOptions({
        reasons: stopReasonConfigs,
        policy: { startedAt, nextRenewalAt, suspendedAt },
        businessRoleId,
        translate: {
          label: reason =>
            t([`${productId}.stopReasons.${reason}`, `stopReasons.${reason}`, reason]),
          // Own tree, not stopReasons.<X>.description: the labels there are strings, so a key
          // cannot be both a label and a parent, and ~14 call sites read them as strings.
          description: reason =>
            t(
              [`${productId}.stopReasonDescriptions.${reason}`, `stopReasonDescriptions.${reason}`],
              {
                defaultValue: '',
                nsSeparator: false,
              },
            ),
          unavailable: unavailability =>
            t(`stopFunnel.reason.unavailable.${unavailability.cause}`, {
              date: 'since' in unavailability ? formatDate(unavailability.since) : undefined,
            }),
        },
      }),
    [stopReasonConfigs, productId, startedAt, nextRenewalAt, suspendedAt, businessRoleId, t],
  );

  const selectedDescription = options.find(({ value }) => value === selected)?.description;

  const goNext = async () => {
    if (!selected) {
      return;
    }
    setSaving(true);
    try {
      await save({ reason: selected });
      const config = stopReasonConfigs.find(({ reason }) => reason === selected);
      // Nothing to ask on the date step when the backend derives the date itself; showing it would
      // only offer a picker whose value is discarded.
      if (config && !asksForADate(resolveEffectiveDate({ reason: config, policy }))) {
        navigate(
          generatePath(config.requiredDocuments?.length ? documentsPath : recapPath, params),
        );
        return;
      }
      next();
    } catch {
      // Stay on the step; the shared MutationCache already shows the API error.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={t('stopFunnel.reason.title')}
        subtitle={t('stopFunnel.reason.subtitle')}
      />

      <Stack gap={2}>
        <SearchableSelect
          searchable
          name="reason"
          options={options}
          value={selected}
          onChange={setPicked}
          placeholder={t('stopFunnel.reason.placeholder')}
        />
        {!!selectedDescription && (
          <Text size="sm" tone="muted">
            {selectedDescription}
          </Text>
        )}
      </Stack>

      <StepperFooter onNext={goNext} nextButtonEnabled={!!selected} loading={saving} />
    </Stack>
  );
}
